/**
 * Вход по одноразовому коду (BS §5.2, экраны A-02 → A-03).
 *
 * V1 — passwordless: код на телефон или email. Ответ сервера не раскрывает,
 * зарегистрирован ли контакт, поэтому UI одинаков для входа и регистрации.
 */
import { useCallback, useState } from 'react'

import { api, authTransitionRequest, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { AuthResult, Challenge, IdentityKind } from '@shared/api/types'
import { getOrCreateInstallId } from '@shared/db'

export interface SignInState {
  challenge: Challenge | null
  /** Код ошибки последней попытки — экран решает по коду, не по тексту. */
  error: string | null
  /** Сколько попыток ввода кода осталось (из `details[].attempts_left`). */
  attemptsLeft: number | null
  pending: boolean
}

const INITIAL: SignInState = { challenge: null, error: null, attemptsLeft: null, pending: false }

export function useSignIn() {
  const [state, setState] = useState<SignInState>(INITIAL)

  /**
   * Шаг 1 (A-02): запрос кода.
   *
   * Изменение v1.12: `delivery` — желаемый канал. Без него решает сервер: код уходит в
   * приложение, если у контакта есть активные входы, иначе в SMS или на почту.
   */
  const requestCode = useCallback(
    async (kind: IdentityKind, identifier: string, delivery?: 'sms' | 'email'): Promise<boolean> => {
      setState((s) => ({ ...s, pending: true, error: null }))

      try {
        const installId = await getOrCreateInstallId()

        const challenge = unwrap(
          await api.POST('/auth/challenges', {
            body: {
              kind,
              identifier,
              install_id: installId,
              platform: 'web',
              ...(delivery ? { delivery } : {}),
            } as never,
          }),
        )

        setState({ challenge, error: null, attemptsLeft: null, pending: false })
        return true
      } catch (error) {
        setState((s) => ({ ...s, pending: false, error: describeRequestError(error, kind) }))
        return false
      }
    },
    [],
  )

  /** Повторная отправка кода — создаётся НОВЫЙ challenge (A-03). */
  const resend = useCallback(
    async (kind: IdentityKind, identifier: string): Promise<boolean> => requestCode(kind, identifier),
    [requestCode],
  )

  /**
   * Изменение v1.12. «Код не пришёл» — перевод доставки на SMS или почту. Ждать
   * `resend_after` не нужно: сервер разрешает смену канала сразу.
   */
  const switchDelivery = useCallback(
    async (kind: IdentityKind, identifier: string): Promise<boolean> => {
      const fallback = state.challenge?.fallback_delivery
      if (!fallback) return false
      return requestCode(kind, identifier, fallback)
    },
    [requestCode, state.challenge],
  )

  /** Шаг 2 (A-03): проверка кода. */
  const verify = useCallback(
    async (
      code: string,
      authTransition = false,
      ensureAuthTransitionOwned?: () => Promise<void>,
    ): Promise<AuthResult | null> => {
      const challenge = state.challenge
      if (!challenge) return null

      setState((s) => ({ ...s, pending: true, error: null }))

      try {
        const installId = await getOrCreateInstallId()
        if (authTransition) {
          if (!ensureAuthTransitionOwned) throw new Error('auth transition guard is required')
          // IDB могла вернуться уже после freeze и потери fallback lease.
          await ensureAuthTransitionOwned()
        }

        const request = {
          params: { path: { challengeId: challenge.challenge_id } },
          ...(authTransition ? authTransitionRequest() : {}),
          body: {
            code,
            device: {
              platform: 'web',
              install_id: installId,
              name: describeBrowser(),
              app_version: __APP_VERSION__,
            },
            // Web получает cookie `__Host-session`; bearer — только Android.
            token_delivery: 'cookie',
          } as never,
        }
        if (authTransition) await ensureAuthTransitionOwned!()
        const response = await api.POST('/auth/challenges/{challengeId}/verify', request)
        if (authTransition) await ensureAuthTransitionOwned!()
        const raw = unwrap(response)

        /*
         * Ответ операции — объединение `AuthResult | StepUpResult` (BS §5.6):
         * при `purpose = step_up` сессия не пересоздаётся и тела `user`/`is_new`
         * в ответе нет. На экране входа это не наш случай, но тип обязан быть сужен.
         */
        if (!isAuthResult(raw)) {
          setState((s) => ({ ...s, pending: false, error: 'Неожиданный ответ сервера' }))
          return null
        }

        setState((s) => ({ ...s, pending: false }))
        return raw
      } catch (error) {
        setState((s) => ({
          ...s,
          pending: false,
          error: describeVerifyError(error),
          attemptsLeft: readAttemptsLeft(error),
          // 410/423 требуют нового challenge — возвращаем на шаг ввода контакта.
          challenge: needsNewChallenge(error) ? null : s.challenge,
        }))
        return null
      }
    },
    [state.challenge],
  )

  const reset = useCallback(() => setState(INITIAL), [])

  return { ...state, requestCode, resend, switchDelivery, verify, reset }
}

/* ===================================================================== */
/* Тексты по кодам (CC §10.1: решение принимает код, не `message`)         */
/* ===================================================================== */

/** Сужение объединения `AuthResult | StepUpResult`. */
function isAuthResult(value: unknown): value is AuthResult {
  return typeof value === 'object' && value !== null && 'user' in value && 'is_new' in value
}

function describeRequestError(error: unknown, kind: IdentityKind): string {
  if (!(error instanceof ApiError)) {
    return 'Не удалось отправить код: нет соединения. Проверьте сеть и попробуйте снова.'
  }

  switch (error.code) {
    case 'validation_failed':
      return kind === 'phone'
        ? 'Сервер не принял номер. Нужен номер с кодом страны, например +7 (900) 123-45-67.'
        : 'Сервер не принял адрес. Проверьте написание — например name@example.com.'
    case 'rate_limited':
      return error.retryAfter
        ? `Слишком много запросов кода. Повторить можно через ${formatSeconds(error.retryAfter)}.`
        : 'Слишком много запросов кода. Подождите несколько минут и попробуйте снова.'
    case 'captcha_required':
    case 'captcha_invalid':
      return 'Нужна дополнительная проверка, что вы не робот. Попробуйте позже или с другой сети.'
    case 'unavailable':
      return 'Сервис временно недоступен. Попробуйте через минуту.'
    default:
      return 'Не удалось отправить код. Попробуйте ещё раз.'
  }
}

function describeVerifyError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return 'Не удалось проверить код: нет соединения. Проверьте сеть.'
  }

  switch (error.code) {
    case 'code_invalid': {
      const left = readAttemptsLeft(error)
      return left !== null && left > 0
        ? `Неверный код. ${left === 1 ? 'Осталась последняя попытка' : `Осталось попыток: ${left}`}.`
        : 'Неверный код.'
    }
    case 'challenge_expired':
      return 'Код устарел — он действует 10 минут. Запросите новый.'
    case 'challenge_locked':
      return 'Попытки исчерпаны. Запросите новый код.'
    case 'rate_limited':
      return error.retryAfter
        ? `Слишком много попыток. Повторите через ${formatSeconds(error.retryAfter)}.`
        : 'Слишком много попыток. Попробуйте позже.'
    default:
      return 'Не удалось войти. Попробуйте ещё раз.'
  }
}

function readAttemptsLeft(error: unknown): number | null {
  if (!(error instanceof ApiError)) return null

  for (const detail of error.details) {
    const value = (detail as { attempts_left?: unknown }).attempts_left
    if (typeof value === 'number') return value
  }
  return null
}

function needsNewChallenge(error: unknown): boolean {
  return (
    error instanceof ApiError && (error.code === 'challenge_expired' || error.code === 'challenge_locked')
  )
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} с`
  return `${Math.ceil(seconds / 60)} мин`
}

/** Имя устройства для списка активных входов (S-06). */
function describeBrowser(): string {
  const ua = navigator.userAgent

  const browser = /Firefox\//.test(ua)
    ? 'Firefox'
    : /Edg\//.test(ua)
      ? 'Edge'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Браузер'

  const platform = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad/.test(ua)
      ? 'iOS'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : ''

  return platform ? `${browser} · ${platform}` : browser
}
